import React, { useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import Button from '@material-ui/core/Button';
import { isMobile, isTablet } from "react-device-detect";

import { useAuth } from '../../../../../../core/contexts/AuthContext';

// Manage > Users listing table. Each row drills into a user-detail tab (addClaimTab) and, with
// edit permission, exposes a Change Password action.
const UserListItem = (props) => {
  const { pageSizeChanged, pageNoChanged, sortFieldChanged, sortField, sortOrder, addClaimTab, passwordReset } = props;
  const items = props.items.Data;
  const paging = props.items.Paging;

  const { chkRolePermissions } = useAuth();

  // The Change Password cell is gated on this permission, which is granted by AuthContext's async
  // /identity/rolepermission fetch. When that grant lands AFTER the table has first rendered,
  // primereact's row memoization keeps the (already-rendered) cell stale, so the action never
  // appears. Key the table on the grant so it remounts once when the permission resolves.
  const canEditUser = chkRolePermissions("Manage", "User Details", "Edit");

  const onPage = (event) => {
    const { page, rows } = event;
    if (Number(paging.PageSize) !== rows) {
      pageSizeChanged(rows);
    } else if (Number(paging.PageSize) !== page + 1) {
      pageNoChanged(page + 1);
    }
  };

  const setUserDetail = (row, val) => {
    return (
      <span
        className="info-sub-head cursor-pointer"
        onClick={() => addClaimTab(row)}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            addClaimTab(row);
          }
        }}
        tabIndex={0}
        role="button"
        aria-label={`View details for ${row.UserCode}`}
      >
        <strong className="opt-l-number">{val}</strong>
      </span>
    );
  };

  const setPasswordContents = (row) => {
    if (!chkRolePermissions("Manage", "User Details", "Edit")) {
      return "";
    }

    return (
      <span
        onClick={() => passwordReset(row)}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            passwordReset(row);
          }
        }}
        tabIndex={0}
        role="button"
        aria-label="Reset password"
      >
        <Button color="primary" style={{ textTransform: 'none' }} variant="text">Change Password</Button>
      </span>
    );
  };

  const setResponsiveContents = (title, contents, isTitle = true) => {
    return (
      <>
        {isTitle && <span className="p-column-title">{title}</span>}
        <span className="p-column-content">{contents}</span>
      </>
    );
  };

  const setNormalDataTable = () => {
    return (
      <DataTable
        key={`users-${canEditUser}`}
        rowHover
        style={{ position: "relative" }}
        size="small"
        value={items}
        className="p-datatable-gridlines  p-datatable-gridlines-custom p-datatable-sm p-datatable-striped p-datatable-striped-custom"
        resizableColumns columnResizeMode="fit"
        paginator
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
        rows={Number(paging.PageSize)}
        rowsPerPageOptions={[10, 20, 50, 100, 500]}
        totalRecords={Number(paging.TotalNumberOfRecords)}
        lazy onPage={onPage} first={Number(paging.PageSize) * (Number(paging.PageNumber) - 1)}
        scrollable scrollHeight="calc(100vh - 390px)"
        onSort={(e) => sortFieldChanged(e)}
        sortField={sortField} sortOrder={sortOrder}
      >
        <Column sortable field="UserCode" header="User Code" body={r => setUserDetail(r, r.UserCode)} />
        <Column sortable field="FirstName" header="Name" body={r => setUserDetail(r, r.FirstName + " " + r.LastName)} />
        <Column sortable field="RoleName" header="Role" />
        <Column sortable field="UserActive" header="Active" />
        <Column field="" header="" body={r => setPasswordContents(r)} />
      </DataTable>
    );
  };

  const setResponsiveDataTable = () => {
    return (
      <DataTable
        key={`users-responsive-${canEditUser}`}
        header='Users'
        style={{ position: "relative" }}
        value={items}
        className="p-datatable-responsive p-datatable-sm "
        paginator
        paginatorTemplate="CurrentPageReport  PageLinks  RowsPerPageDropdown"
        currentPageReportTemplate=" {first} to {last} of {totalRecords}"
        rows={Number(paging.PageSize)}
        rowsPerPageOptions={[10, 20, 50, 100, 500]}
        totalRecords={Number(paging.TotalNumberOfRecords)}
        lazy onPage={onPage} first={Number(paging.PageSize) * (Number(paging.PageNumber) - 1)}
      >
        <Column header="User Code" body={r => setResponsiveContents('User Code', setUserDetail(r, r.UserCode))} />
        <Column header="User Name" body={r => setResponsiveContents('User Name', setUserDetail(r, r.FirstName + " " + r.LastName))} />
        <Column header="Active" body={r => setResponsiveContents('Active', r.UserActive)} />
        <Column header="Role Name" body={r => setResponsiveContents('Role Name', r.RoleName)} />
        <Column header="" body={r => setPasswordContents(r)} />
      </DataTable>
    );
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const datatableList = document.getElementsByClassName("p-datatable-scrollable-body");
    if (datatableList.length > 0) {
      datatableList[0].scrollTop = 0;
    }
  }, [items]);

  const setDataTable = () => {
    if (isMobile && !isTablet) {
      return setResponsiveDataTable();
    }
    return setNormalDataTable();
  };

  return (
    <div
      className="perfect-scrollbar"
      style={{ position: "relative", width: "100%", height: "100%" }}
    >
      {setDataTable()}
    </div>
  );
};

export default UserListItem;
